import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { invoiceTransactionTypeService } from '@services/invoiceTransactionTypeService';
import { companyService } from '@services/companyService';
import { Company, formatCnpj } from '@/types/company';
import {
  InvoiceTransactionTypeFormData,
  TransactionType,
} from '@/types/invoiceTransactionType';

export const InvoiceTransactionTypeFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<InvoiceTransactionTypeFormData>({
    invoice_transaction_type_name: '',
    transaction_type: null,
    final_customer: null,
    active: true,
    is_billable: true,
    cfop_operation: null,
    erp_mappings: [],
  });

  // Carrega empresas
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const data = await companyService.getCompanies();
        setCompanies(data);
      } catch (err) {
        console.error('Erro ao carregar empresas:', err);
      }
    };
    loadCompanies();
  }, []);

  // Após carregar empresas, inicializar mapeamentos
  useEffect(() => {
    if (companies.length > 0 && !isEditing && formData.erp_mappings.length === 0) {
      setFormData((prev) => ({
        ...prev,
        erp_mappings: companies.map((c) => ({
          company_id: c.company_id,
          erp_id: '',
        })),
      }));
    }
  }, [companies, isEditing]);

  // Carrega dados para edição
  useEffect(() => {
    if (isEditing && id) {
      loadData(parseInt(id));
    }
  }, [id, isEditing]);

  const loadData = async (itemId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await invoiceTransactionTypeService.getById(itemId);
      setFormData({
        invoice_transaction_type_name: data.invoice_transaction_type_name,
        transaction_type: data.transaction_type ?? null,
        final_customer: data.final_customer ?? null,
        active: data.active ?? true,
        is_billable: data.is_billable ?? true,
        cfop_operation: data.cfop_operation ?? null,
        erp_mappings: companies.length > 0
          ? companies.map((c) => {
              const existing = data.erp_mappings.find(
                (m) => m.company_id === c.company_id,
              );
              return {
                company_id: c.company_id,
                erp_id: existing ? existing.erp_id : '',
              };
            })
          : data.erp_mappings.map((m) => ({
              company_id: m.company_id,
              erp_id: m.erp_id,
            })),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Recarrega dados quando empresas são carregadas e estamos editando
  useEffect(() => {
    if (isEditing && id && companies.length > 0) {
      loadData(parseInt(id));
    }
  }, [companies]);

  const updateField = (field: keyof InvoiceTransactionTypeFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const updateErpMapping = (companyId: number, erpId: string) => {
    setFormData((prev) => ({
      ...prev,
      erp_mappings: prev.erp_mappings.map((m) =>
        m.company_id === companyId ? { ...m, erp_id: erpId } : m,
      ),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.invoice_transaction_type_name.trim()) {
      newErrors.invoice_transaction_type_name = 'Nome é obrigatório';
    } else if (formData.invoice_transaction_type_name.length > 150) {
      newErrors.invoice_transaction_type_name = 'Nome deve ter no máximo 150 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      // Filtra apenas mapeamentos preenchidos
      const payload: InvoiceTransactionTypeFormData = {
        ...formData,
        erp_mappings: formData.erp_mappings.filter(
          (m) => m.erp_id !== '' && m.erp_id !== null && m.erp_id !== undefined,
        ).map((m) => ({
          company_id: m.company_id,
          erp_id: typeof m.erp_id === 'string' ? parseInt(m.erp_id) || 0 : m.erp_id,
        })),
      };

      if (isEditing && id) {
        await invoiceTransactionTypeService.update(parseInt(id), payload);
      } else {
        await invoiceTransactionTypeService.create(payload);
      }

      navigate('/settings/fiscal/invoice-transaction-types');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const getCompanyName = (companyId: number): string => {
    const company = companies.find((c) => c.company_id === companyId);
    return company ? `${company.name} — ${formatCnpj(company.document)}` : `Empresa ${companyId}`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/settings/fiscal/invoice-transaction-types')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          onClick={() => navigate('/settings/fiscal/invoice-transaction-types')}
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">
            {isEditing ? 'Editar Natureza da Operação' : 'Nova Natureza da Operação'}
          </h1>
          <p className="text-slate-600">
            {isEditing
              ? 'Altere as informações da natureza de operação fiscal'
              : 'Preencha os dados para cadastrar uma nova natureza de operação fiscal'}
          </p>
        </div>
      </div>

      {/* Erro geral */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário */}
      <Card>
        <CardHeader title="Dados da Natureza de Operação" />
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3">
              <Input
                label="Nome da Natureza de Operação *"
                placeholder="Ex: Venda de Mercadoria"
                value={formData.invoice_transaction_type_name}
                onChange={(e) =>
                  updateField('invoice_transaction_type_name', e.target.value)
                }
                error={errors.invoice_transaction_type_name}
                maxLength={150}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Tipo de Transação
              </label>
              <select
                className="input w-full text-sm"
                value={formData.transaction_type ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  updateField('transaction_type', val === '' ? null : val as TransactionType);
                }}
              >
                <option value="">— Não informado</option>
                <option value="E">↓ Entrada</option>
                <option value="S">↑ Saída</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Indica a direção da operação fiscal
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Destinado a Consumidor Final?
              </label>
              <select
                className="input w-full text-sm"
                value={formData.final_customer === null || formData.final_customer === undefined ? '' : formData.final_customer.toString()}
                onChange={(e) => {
                  const val = e.target.value;
                  updateField('final_customer', val === '' ? null : val === 'true');
                }}
              >
                <option value="">— Não informado</option>
                <option value="true">Sim — Consumidor Final</option>
                <option value="false">Não — Revenda / Transferência</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Utilizado pelo ERP na emissão da NF-e (tributação ICMS)
              </p>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => updateField('active', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600" />
                <span className="ml-3 text-sm font-medium text-slate-700">
                  {formData.active ? 'Ativo' : 'Inativo'}
                </span>
              </label>
              <p className="text-xs text-slate-400">
                Naturezas inativas não aparecem no formulário de criação de nota fiscal
              </p>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_billable}
                  onChange={(e) => updateField('is_billable', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600" />
                <span className="ml-3 text-sm font-medium text-slate-700">
                  {formData.is_billable ? 'Faturada' : 'Não faturada'}
                </span>
              </label>
              <p className="text-xs text-slate-400">
                Marque para operações de venda. Desmarque para operações como
                Remessa em bonificação, doação ou brinde.
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Operação fiscal (CFOP)
              </label>
              <select
                className="w-full md:w-96 input-field text-sm"
                value={formData.cfop_operation ?? ''}
                onChange={(e) => updateField('cfop_operation', e.target.value || null)}
              >
                <option value="">Não definir (derivar da emissão)</option>
                <option value="SALE">Venda de mercadoria — 5.102 / 6.102</option>
                <option value="RETURN">Devolução de venda (entrada) — 1.202 / 2.202</option>
                <option value="GIFT">Remessa em bonificação/doação/brinde — 5.910 / 6.910</option>
                <option value="OTHER_OUTPUT">Outra saída de mercadoria — 5.949 / 6.949</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Define o CFOP das notas emitidas com esta natureza (dentro do estado / interestadual).
                Sem definição, a emissão deriva pelo modo da nota (venda/bonificação/devolução) — comportamento anterior.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mapeamentos ERP por Empresa */}
      <Card>
        <CardHeader title="ID da Natureza de Operação no Bling (por empresa)" />
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">
            Informe o ID correspondente da natureza de operação no Bling para cada empresa do grupo.
            Deixe em branco se não houver mapeamento para a empresa.
          </p>
          <div className="space-y-3">
            {formData.erp_mappings.length === 0 && companies.length === 0 ? (
              <p className="text-sm text-slate-400">Carregando empresas...</p>
            ) : (
              formData.erp_mappings.map((mapping) => (
                <div
                  key={mapping.company_id}
                  className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200"
                >
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700">
                      {getCompanyName(mapping.company_id)}
                    </label>
                    <span className="text-xs text-slate-400">
                      Empresa ID: {mapping.company_id}
                    </span>
                  </div>
                  <div className="w-48">
                    <Input
                      type="number"
                      placeholder="ID no Bling"
                      value={mapping.erp_id}
                      onChange={(e) =>
                        updateErpMapping(mapping.company_id, e.target.value)
                      }
                      min={0}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate('/settings/fiscal/invoice-transaction-types')}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          icon={<Save size={20} />}
        >
          {saving
            ? 'Salvando...'
            : isEditing
            ? 'Salvar Alterações'
            : 'Criar Natureza de Operação'}
        </Button>
      </div>
    </div>
  );
};
